import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useMotionValue, useReducedMotion, useScroll, useSpring, useTransform } from 'framer-motion';
import '@fontsource/barlow-condensed/700.css';
import '@fontsource/barlow-condensed/700-italic.css';
import '@fontsource/barlow-condensed/800.css';
import '@fontsource/barlow-condensed/800-italic.css';
import useBrandLogo from '../../hooks/useBrandLogo';
import PanelPreview from '../../img/landing-panel-dashboard.jpg';
import { InkStage } from './landing/InkStage';
import { ComposerDemo } from './landing/ComposerDemo';
import { FeatureBento } from './landing/FeatureBento';
import { SurveyDemo } from './landing/SurveyDemo';
import { ArchitectureDiagram } from './landing/ArchitectureDiagram';
import { DemoRequestModal } from './landing/DemoRequestModal';
import { PricingSection } from './landing/PricingSection';
import { Cursor, EASE_OUT, Magnetic, MaskedHeading, Marquee, Reveal, SignatureStroke } from './landing/primitives';
import { useT } from '../../i18n';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';
import './LandingPage.css';

const messages = {
  nav: {
    tr: [['#demo', 'Ürün'], ['#ozellikler', 'Özellikler'], ['#guvenlik', 'Güvenlik'], ['#fiyatlar', 'Fiyatlar'], ['#sss', 'SSS']],
    en: [['#demo', 'Product'], ['#ozellikler', 'Features'], ['#guvenlik', 'Security'], ['#fiyatlar', 'Pricing'], ['#sss', 'FAQ']],
  },
  story: {
    tr: [
      {
        n: '01',
        title: ['Yüzlerce çalışan.', 'Yüzlerce farklı imza.'],
        text: 'Eski logolar, güncellenmemiş unvanlar, herkesin kendi seçtiği renkler, unutulan yasal uyarılar. Her biri markanızdan sessizce bir parça eksiltir.',
      },
      {
        n: '02',
        title: ['Tek panel.', 'Tek doğru.'],
        text: 'Şablonlar, gruplar, kampanyalar ve yasal metinler tek merkezde. Bir kez değiştirin; tüm şirkette aynı anda geçerli olsun.',
      },
      {
        n: '03',
        title: ['Her gönderimde', 'kusursuz.'],
        text: 'Yeni ileti penceresi açıldığı anda Outlook eklentisi doğru imzayı ekler. Çalışan hiçbir şey yapmaz, markanız hiçbir şeyi şansa bırakmaz.',
      },
    ],
    en: [
      {
        n: '01',
        title: ['Hundreds of employees.', 'Hundreds of signatures.'],
        text: 'Old logos, outdated job titles, everyone’s favourite colours, forgotten disclaimers. Each one quietly chips away at your brand.',
      },
      {
        n: '02',
        title: ['One panel.', 'One source of truth.'],
        text: 'Templates, groups, campaigns and legal text in one place. Change it once and it’s live across the whole company at the same moment.',
      },
      {
        n: '03',
        title: ['Flawless,', 'every single send.'],
        text: 'The moment a new message window opens, the Outlook add-in inserts the right signature. Employees do nothing; your brand leaves nothing to chance.',
      },
    ],
  },
  platforms: {
    tr: ['Outlook Masaüstü', 'Outlook Web', 'Yeni Outlook', 'Outlook iOS', 'Outlook Android', 'Outlook Mac', 'Microsoft 365', 'Exchange Online'],
    en: ['Outlook Desktop', 'Outlook on the web', 'New Outlook', 'Outlook iOS', 'Outlook Android', 'Outlook Mac', 'Microsoft 365', 'Exchange Online'],
  },
  steps: {
    tr: [
      { n: '01', title: 'Bağlayın', text: 'Microsoft hesabınızla giriş yapın, kurum dizininizi tek onayla bağlayın. Kullanıcılar, gruplar ve unvanlar kendiliğinden gelir.' },
      { n: '02', title: 'Tasarlayın', text: 'Editörde şablonlarınızı hazırlayın; gruplara, dillere ve kişilere atayın. Canlı önizleme gerçek verilerle gösterir.' },
      { n: '03', title: 'Yayınlayın', text: 'Eklentiyi Microsoft 365 yönetim merkezinden tüm kuruma ya da önce bir pilot gruba dağıtın. Tek seferlik, birkaç dakika.' },
    ],
    en: [
      { n: '01', title: 'Connect', text: 'Sign in with your Microsoft account and connect your directory with a single consent. Users, groups and job titles flow in on their own.' },
      { n: '02', title: 'Design', text: 'Build your templates in the editor and assign them to groups, languages and individuals. The live preview renders them with real data.' },
      { n: '03', title: 'Publish', text: 'Deploy the add-in from the Microsoft 365 admin center to the whole organization, or to a pilot group first. One time, a few minutes.' },
    ],
  },
  security: {
    tr: [
      ['Kurum bazında izolasyon', 'Her organizasyonun verisi ayrı tutulur; bir kurum diğerinin hiçbir kaydını göremez.'],
      ['Microsoft ile giriş', 'Şifre yok. Panele kurumsal Microsoft hesabınızla, kendi kimlik politikalarınızla girersiniz.'],
      ['Rol bazlı yetki', 'Kurum yöneticisi ve kullanıcı yetkileri ayrıdır; yönetim uçları yalnızca yetkili rollere açıktır.'],
      ['Denetim günlüğü', 'Kullanıcı, rol ve hesap değişiklikleri kimin ne zaman yaptığıyla birlikte kayıt altındadır.'],
      ['KVKK’ya uygun saklama', 'Teslimat kayıtları 90 gün sonra otomatik silinir; yalnızca gerekli olan tutulur.'],
      ['Uçtan uca şifreleme', 'Panel, eklenti ve API arasındaki tüm trafik HTTPS üzerinden şifrelenir.'],
    ],
    en: [
      ['Tenant isolation', 'Every organization’s data is kept separate; no organization can see a single record of another.'],
      ['Sign in with Microsoft', 'No passwords. You enter the panel with your company Microsoft account, under your own identity policies.'],
      ['Role-based access', 'Organization admin and user permissions are separate; management endpoints are open only to authorized roles.'],
      ['Audit log', 'User, role and account changes are recorded along with who made them and when.'],
      ['Minimal data retention', 'Delivery records are deleted automatically after 90 days, in line with Turkey’s data protection law (KVKK).'],
      ['Encrypted in transit', 'All traffic between the panel, the add-in and the API is encrypted over HTTPS.'],
    ],
  },
  faqs: {
    tr: [
      { q: '14 günlük deneme ne zaman başlıyor?', a: 'İlk imzanız bir e-postaya eklendiği anda. Hesabı açmak, Microsoft 365’i bağlamak ve şablonunuzu hazırlamak deneme süresinden yemez; kredi kartı da istenmez.' },
      { q: 'Fiyatlandırma nasıl işliyor?', a: 'Kullanıcı başı aylık 2 dolar, yıllık ödemede %20 indirim; fiyatlara KDV dahil değildir. Ödeme, günün TCMB kuruyla Türk lirası olarak kartınızdan alınır ve abonelik dönem sonunda otomatik yenilenir. Yenilemeyi panelden istediğiniz zaman kapatabilirsiniz.' },
      { q: 'Çalışanların bir şey kurması gerekiyor mu?', a: 'Hayır. Eklenti Microsoft 365 yönetim merkezinden tüm kuruma (ya da önce bir pilot gruba) merkezi olarak dağıtılır. Kullanıcılar hiçbir ayar yapmaz; imza, yeni ileti açıldığı anda kendiliğinden eklenir.' },
      { q: 'Hangi Outlook sürümlerinde çalışır?', a: 'Windows ve Mac için Outlook masaüstü, Outlook Web, yeni Outlook ve Outlook’un iOS ile Android uygulamalarında çalışır.' },
      { q: 'E-postalarımız sizin sunucularınızdan geçiyor mu?', a: 'Hayır. Eklenti imzayı yalnızca gönderenin adresiyle ister. E-posta içeriği, ekler ve alıcılar Martinci’ye hiç gönderilmez; mesajınız doğrudan Microsoft 365 üzerinden iletilir.' },
      { q: 'Birden fazla alan adını yönetebilir miyim?', a: 'Evet. Aynı Microsoft 365 kiracısı altındaki tüm alan adlarınızı tek panelden yönetebilirsiniz.' },
      { q: 'İmzalarımızı çok dilli yapabilir miyim?', a: 'Evet. Her şablonun yedi dile kadar varyantını hazırlayabilirsiniz. Kullanıcılar dillerini İmzam sayfasından ya da Outlook içinden seçebilir.' },
      { q: 'Kampanya banner’ları nasıl çalışıyor?', a: 'Kampanyayı içerik takvimine eklersiniz ve hangi gruplarda görüneceğini seçersiniz. Başlangıç tarihinde imzalara kendiliğinden eklenir, bitişte kendiliğinden kalkar.' },
    ],
    en: [
      { q: 'When does the 14-day trial start?', a: 'The moment your first signature is added to an email. Opening the account, connecting Microsoft 365 and building your template don’t count against the trial, and no credit card is needed.' },
      { q: 'How does pricing work?', a: '$2 per user per month, 20% off when paid yearly; prices exclude VAT. You’re charged in Turkish lira at the day’s TCMB rate, and the subscription renews automatically at the end of each term. You can switch auto-renewal off in the dashboard at any time.' },
      { q: 'Do employees need to install anything?', a: 'No. The add-in is deployed centrally from the Microsoft 365 admin center to the whole organization (or to a pilot group first). Users configure nothing; the signature appears by itself the moment they open a new message.' },
      { q: 'Which Outlook versions does it work with?', a: 'Outlook desktop for Windows and Mac, Outlook on the web, the new Outlook, and Outlook’s iOS and Android apps.' },
      { q: 'Do our emails pass through your servers?', a: 'No. The add-in requests the signature with nothing but the sender’s address. Message content, attachments and recipients are never sent to Martinci; your message goes straight through Microsoft 365.' },
      { q: 'Can I manage multiple domains?', a: 'Yes. You can manage every domain under the same Microsoft 365 tenant from a single panel.' },
      { q: 'Can our signatures be multilingual?', a: 'Yes. Every template can have variants in up to seven languages. Users pick their language on the My Signature page or from within Outlook.' },
      { q: 'How do campaign banners work?', a: 'Add the campaign to the content calendar and choose which groups see it. It appears in signatures automatically on the start date and disappears on the end date.' },
    ],
  },
  scroll: { tr: 'Kaydırın', en: 'Scroll' },
  panelAlt: { tr: 'Martinci yönetim paneli: personeller, gruplar ve imza şablonları', en: 'Martinci admin panel: people, groups and signature templates' },
  docTitle: { tr: 'Martinci İmza — Microsoft 365 için kurumsal e-posta imza yönetimi', en: 'Martinci Signature Manager — Email signatures for Microsoft 365' },
  homeAria: { tr: 'Martinci İmza anasayfa', en: 'Martinci home' },
  sectionsAria: { tr: 'Sayfa bölümleri', en: 'Page sections' },
  login: { tr: 'Giriş yap', en: 'Sign in' },
  bookDemo: { tr: 'Demo planlayın', en: 'Book a demo' },
  startTrial: { tr: 'Ücretsiz deneyin', en: 'Start free trial' },
  startTrialLong: { tr: '14 gün ücretsiz deneyin', en: 'Try it free for 14 days' },
  trialNote: {
    tr: 'Kredi kartı gerekmez · Deneme süresi ilk imzanız gönderildiğinde başlar',
    en: 'No credit card · Your trial starts when your first signature goes out',
  },
  heroLines: {
    tr: ['Her e-posta', 'markanızı', [{ text: 'imzalar.', className: 'lp-accent' }]],
    en: ['Every email,', 'signed by', [{ text: 'your brand.', className: 'lp-accent' }]],
  },
  heroLead: {
    tr: 'Kurumunuzdaki tüm e-posta imzalarını tek panelden tasarlayın, gruplara atayın, kampanyalarla canlandırın. Outlook eklentisi doğru imzayı her yeni iletiye — masaüstünde, web’de ve mobilde — kendiliğinden ekler.',
    en: 'Design every email signature in your organization from one panel, assign them to groups and bring them to life with campaigns. The Outlook add-in adds the right signature to every new message — on desktop, web and mobile — automatically.',
  },
  hello: { tr: 'Merhaba', en: 'Hello' },
  facts: {
    tr: [['0', 'Çalışan tarafında kurulum'], ['4', 'Platform: Masaüstü · Web · iOS · Android'], ['7', 'Dilde şablon varyantı'], ['0 bayt', 'E-posta içeriği sunucumuza gelir']],
    en: [['0', 'Installs on the employee side'], ['4', 'Platforms: Desktop · Web · iOS · Android'], ['7', 'Template languages'], ['0 bytes', 'Of email content reach our servers']],
  },
  storyAria: { tr: 'Neden Martinci', en: 'Why Martinci' },
  demoEyebrow: { tr: 'Canlı demo', en: 'Live demo' },
  demoLines: {
    tr: ['Yeni ileti açın.', [{ text: 'İmza hazır.', className: 'lp-accent-ink' }]],
    en: ['Open a new message.', [{ text: 'Signature ready.', className: 'lp-accent-ink' }]],
  },
  demoText: {
    tr: 'Çalışan hiçbir şey kurmaz, hiçbir şey seçmez. Outlook’ta yeni ileti penceresi açıldığı anda eklenti kişinin grubuna, diline ve kampanya takvimine göre doğru imzayı ekler. Bir çalışan seçin ve izleyin.',
    en: 'Employees install nothing and choose nothing. The moment a new message window opens in Outlook, the add-in inserts the right signature for the person’s group, language and campaign calendar. Pick an employee and watch.',
  },
  featuresEyebrow: { tr: 'Özellikler', en: 'Features' },
  featuresLines: { tr: ['İmzadan fazlası:', 'bir iletişim kanalı.'], en: ['More than a signature:', 'a communication channel.'] },
  featuresText: {
    tr: 'Her gün şirketinizden binlerce e-posta çıkıyor. Martinci her birini tutarlı bir marka temas noktasına, bir kampanya alanına ve bir geri bildirim fırsatına dönüştürür.',
    en: 'Thousands of emails leave your company every day. Martinci turns each one into a consistent brand touchpoint, a campaign slot and a chance for feedback.',
  },
  surveyEyebrow: { tr: 'Memnuniyet anketleri', en: 'Satisfaction surveys' },
  surveyLines: { tr: ['Her e-posta', 'bir geri bildirim.'], en: ['Every email,', 'a feedback loop.'] },
  surveyText: {
    tr: 'İmzaya tek tıklık bir memnuniyet anketi ekleyin. Alıcı hiçbir şey kurmadan puan verir; olumsuz bir yanıt geldiğinde belirlediğiniz kişi anında haberdar olur.',
    en: 'Add a one-click satisfaction survey to the signature. Recipients rate without installing anything, and when a negative response comes in, the person you choose hears about it instantly.',
  },
  surveyPoints: {
    tr: ['Yıldız veya emoji ölçeği, 3 ya da 5 puan', 'Olumsuz yanıtta anında e-posta alarmı ve takip aksiyonu', 'Şablon ve grup bazında dağılım raporu', 'Tahmin edilemeyen, imzalı anket bağlantıları'],
    en: ['Star or emoji scale, 3 or 5 points', 'Instant email alert and a follow-up action on negative responses', 'Breakdown reports by template and group', 'Signed, unguessable survey links'],
  },
  surveyTry: { tr: 'Deneyin: bir yıldıza dokunun', en: 'Try it: tap a star' },
  setupEyebrow: { tr: 'Kurulum', en: 'Setup' },
  setupLines: { tr: ['Üç adım.', 'Bir öğleden sonra.'], en: ['Three steps.', 'One afternoon.'] },
  securityEyebrow: { tr: 'Güvenlik & mimari', en: 'Security & architecture' },
  securityLines: {
    tr: ['E-postanız', [{ text: 'bize hiç uğramaz.', className: 'lp-accent' }]],
    en: ['Your email', [{ text: 'never touches us.', className: 'lp-accent' }]],
  },
  securityText: {
    tr: 'Sunucu taraflı imza çözümleri e-postanızı kendi altyapılarından geçirerek imza ekler. Martinci’de imza Outlook’un içinde, ileti yazılırken eklenir; mesajınız doğrudan Microsoft 365’e gider.',
    en: 'Server-side signature tools route your email through their own infrastructure to stamp the signature on. With Martinci, the signature is added inside Outlook while the message is being written, and your message goes straight to Microsoft 365.',
  },
  faqEyebrow: { tr: 'SSS', en: 'FAQ' },
  pricingEyebrow: { tr: 'Fiyatlar', en: 'Pricing' },
  pricingLines: { tr: ['Kullanıcı başına,', [{ text: 'tek fiyat.', className: 'lp-accent' }]], en: ['One price,', [{ text: 'per user.', className: 'lp-accent' }]] },
  faqLines: { tr: ['Aklınızdaki', 'sorular.'], en: ['Questions', 'on your mind.'] },
  faqMissing: { tr: 'Sorunuz burada yok mu?', en: 'Question not listed?' },
  ctaKicker: { tr: 'Hazır olduğunuzda', en: 'Whenever you’re ready' },
  ctaLines: {
    tr: ['Markanız,', [{ text: 'her e-postada.', className: 'lp-accent' }]],
    en: ['Your brand,', [{ text: 'in every email.', className: 'lp-accent' }]],
  },
  ctaCursor: { tr: 'Başlayın', en: 'Let’s go' },
  footerAria: { tr: 'Alt bağlantılar', en: 'Footer links' },
  legalAria: { tr: 'Yasal', en: 'Legal' },
  contact: { tr: 'İletişim', en: 'Contact' },
  legalLinks: {
    tr: [['/kvkk-aydinlatma-metni', 'KVKK'], ['/gizlilik-politikasi', 'Gizlilik'], ['/kullanim-sartlari', 'Kullanım Şartları'], ['/mesafeli-satis-sozlesmesi', 'Mesafeli Satış'], ['/cerez-politikasi', 'Çerezler']],
    en: [['/kvkk-aydinlatma-metni', 'KVKK Notice'], ['/gizlilik-politikasi', 'Privacy'], ['/kullanim-sartlari', 'Terms of Use'], ['/mesafeli-satis-sozlesmesi', 'Distance Sales'], ['/cerez-politikasi', 'Cookies']],
  },
  portalName: { tr: 'İmza Yönetim Portalı', en: 'Signature Manager' },
};

// Story progress (0–1) at which each copy step is on screen — matches
// InkStage's storyCurve (step changes at morph 1.5 and 2.5).
const STORY_SEGMENTS = [[0, 0.25], [0.25, 0.7], [0.7, 1]];

// One step of the story rail: fills continuously while you scroll through
// its stretch, so there is visible feedback even while the particles hold.
function RailSegment({ label, range, progress, active }) {
  const fill = useTransform(progress, range, [0, 1], { clamp: true });
  return (
    <span className={active ? 'is-on' : ''}>
      <motion.i style={{ scaleX: fill }} />
      {label}
    </span>
  );
}

function StoryMeter({ progress }) {
  const { t } = useT(messages);
  const hint = useTransform(progress, [0, 0.9, 1], [1, 1, 0]);
  const pct = useTransform(progress, (v) => `${Math.round(v * 100)}`);
  return (
    <div className="lp-story-meter" aria-hidden="true">
      <motion.span className="lp-story-meter-pct">{pct}</motion.span>
      <div className="lp-story-meter-track">
        <motion.i style={{ scaleY: progress }} />
      </div>
      <motion.span className="lp-story-meter-hint" style={{ opacity: hint }}>
        {t('scroll')} <span>↓</span>
      </motion.span>
    </div>
  );
}

function SectionHead({ index, eyebrow, lines, text, align = 'left', dark = false }) {
  return (
    <div className={`lp-head is-${align} ${dark ? 'is-dark' : ''}`}>
      <Reveal className="lp-head-meta">
        <span className="lp-head-index">({index})</span>
        <span className="lp-head-eyebrow">{eyebrow}</span>
      </Reveal>
      <MaskedHeading lines={lines} className="lp-head-title" />
      {text && <Reveal delay={0.15} as="p" className="lp-head-text">{text}</Reveal>}
    </div>
  );
}

function FaqItem({ q, a, i }) {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className={`lp-faq-item ${open ? 'is-open' : ''}`}>
      <button className="lp-faq-q" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className="lp-faq-n">{String(i + 1).padStart(2, '0')}</span>
        <span className="lp-faq-text">{q}</span>
        <span className="lp-faq-icon" aria-hidden="true" />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            className="lp-faq-a"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: EASE_OUT }}
          >
            <p>{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function PanelShowcase() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'center center'] });
  const rotateX = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [28, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], reduce ? [1, 1] : [0.86, 1]);
  const y = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [80, 0]);
  const { t } = useT(messages);
  return (
    <div className="lp-panel" ref={ref}>
      <motion.div className="lp-panel-frame" style={{ rotateX, scale, y }}>
        <div className="lp-panel-bar"><i /><i /><i /><span>imza.martinci.com</span></div>
        <div className="lp-panel-shot">
          <img src={PanelPreview} alt={t('panelAlt')} loading="lazy" />
        </div>
      </motion.div>
    </div>
  );
}

export const LandingPage = () => {
  const { t, lang } = useT(messages);
  const logo = useBrandLogo('white');
  const story = t('story');
  const stageRef = useRef(null);
  const heroRef = useRef(null);
  const storyRef = useRef(null);
  const [step, setStep] = useState(0);
  const [navHidden, setNavHidden] = useState(false);
  const [navSolid, setNavSolid] = useState(false);
  const onStep = useCallback((s) => setStep(s), []);
  const storyProgress = useMotionValue(0);
  const onStoryProgress = useCallback((p) => storyProgress.set(p), [storyProgress]);
  const { scrollYProgress: pageProgress } = useScroll();
  const pageBar = useSpring(pageProgress, { stiffness: 220, damping: 32, restDelta: 0.001 });

  // Every "Demo planlayın" CTA opens the request form; /?demo=1 (used by the
  // login page) opens it on arrival.
  const [demoOpen, setDemoOpen] = useState(false);
  const openDemo = useCallback(() => setDemoOpen(true), []);
  const closeDemo = useCallback(() => setDemoOpen(false), []);
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('demo') === '1') {
      setDemoOpen(true);
      navigate('/', { replace: true });
    }
  }, [location.search, navigate]);

  const { scrollYProgress: heroProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(heroProgress, [0, 1], [0, -160]);
  const heroOpacity = useTransform(heroProgress, [0, 0.75], [1, 0]);

  const docTitle = t('docTitle');
  useEffect(() => {
    const prevTitle = document.title;
    document.title = docTitle;
    return () => { document.title = prevTitle; };
  }, [docTitle]);

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setNavSolid(y > 40);
      setNavHidden(y > lastY && y > 400);
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <div className="lp" lang={lang}>
      <Cursor />
      <div className="lp-grain" aria-hidden="true" />
      <motion.div className="lp-progress" style={{ scaleX: pageBar }} aria-hidden="true" />
      <DemoRequestModal open={demoOpen} onClose={closeDemo} />

      <header className={`lp-nav ${navHidden ? 'is-hidden' : ''} ${navSolid ? 'is-solid' : ''}`}>
        <Link to="/" className="lp-nav-brand" aria-label={t('homeAria')}>
          <img src={logo.src} alt={logo.alt} />
        </Link>
        <nav className="lp-nav-links" aria-label={t('sectionsAria')}>
          {t('nav').map(([href, label]) => <a key={href} href={href}>{label}</a>)}
        </nav>
        <div className="lp-nav-actions">
          <LanguageSwitcher className="lp-nav-lang" />
          <Link to="/login" className="lp-nav-login">{t('login')}</Link>
          <Link to="/basla" className="lp-btn lp-btn-sm">{t('startTrial')}</Link>
        </div>
      </header>

      <div className="lp-stage" ref={stageRef}>
        <InkStage storyRef={storyRef} stageRef={stageRef} onStep={onStep} onProgress={onStoryProgress} />

        <section className="lp-hero" ref={heroRef}>
          <motion.div className="lp-hero-inner" style={{ y: heroY, opacity: heroOpacity }}>
            <div className="lp-hero-title-wrap">
              <MaskedHeading
                as="h1"
                immediate
                delay={0.35}
                stagger={0.09}
                className="lp-hero-title"
                key={lang}
                lines={t('heroLines')}
              />
              <SignatureStroke className="lp-hero-stroke" delay={1.3} />
            </div>

            <motion.div
              className="lp-hero-bottom"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: EASE_OUT, delay: 1.1 }}
            >
              <p className="lp-hero-lead">{t('heroLead')}</p>
              <div className="lp-hero-cta-col">
                <div className="lp-hero-ctas">
                  <Magnetic>
                    <Link to="/basla" className="lp-btn lp-btn-lg" data-cursor={t('hello')}>
                      {t('startTrialLong')} <span className="lp-btn-arrow">→</span>
                    </Link>
                  </Magnetic>
                  <button type="button" className="lp-btn-ghost" onClick={openDemo}>{t('bookDemo')}</button>
                </div>
                <p className="lp-trial-note">{t('trialNote')}</p>
              </div>
            </motion.div>
          </motion.div>

          <motion.ul
            className="lp-hero-facts"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.6 }}
          >
            {t('facts').map(([n, label]) => <li key={label}><strong>{n}</strong><span>{label}</span></li>)}
          </motion.ul>

          <div className="lp-scroll-hint" aria-hidden="true"><span /></div>
        </section>

        <section className="lp-story" ref={storyRef} aria-label={t('storyAria')}>
          <div className="lp-story-sticky">
            <StoryMeter progress={storyProgress} />
            <div className="lp-story-copy">
              <div className="lp-story-rail" aria-hidden="true">
                {story.map((s, i) => (
                  <RailSegment key={s.n} label={s.n} range={STORY_SEGMENTS[i]} progress={storyProgress} active={i === step} />
                ))}
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={`${lang}-${step}`}
                  className="lp-story-step"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, transition: { duration: 0.25 } }}
                >
                  <h2 className="lp-story-title">
                    {story[step].title.map((line, i) => (
                      <span className="lp-masked-line" key={line}>
                        <motion.span
                          initial={{ y: '105%' }}
                          animate={{ y: '0%' }}
                          transition={{ duration: 0.9, ease: EASE_OUT, delay: i * 0.08 }}
                        >
                          {line}
                        </motion.span>
                      </span>
                    ))}
                  </h2>
                  <motion.p
                    className="lp-story-text"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: EASE_OUT, delay: 0.2 }}
                  >
                    {story[step].text}
                  </motion.p>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </section>
      </div>

      <main className="lp-content">
        <div className="lp-paper">
          <div className="lp-marquee-band">
            <Marquee items={t('platforms')} />
          </div>

          <section className="lp-section" id="demo">
            <SectionHead
              index="01"
              eyebrow={t('demoEyebrow')}
              lines={t('demoLines')}
              text={t('demoText')}
            />
            <ComposerDemo />
          </section>

          <section className="lp-section" id="ozellikler">
            <SectionHead
              index="02"
              eyebrow={t('featuresEyebrow')}
              lines={t('featuresLines')}
              text={t('featuresText')}
            />
            <FeatureBento />
          </section>
        </div>

        <section className="lp-survey">
          <div className="lp-survey-inner">
            <div className="lp-survey-copy">
              <SectionHead
                index="03"
                eyebrow={t('surveyEyebrow')}
                dark
                lines={t('surveyLines')}
                text={t('surveyText')}
              />
              <Reveal as="ul" className="lp-survey-points">
                {t('surveyPoints').map((p) => <li key={p}>{p}</li>)}
              </Reveal>
              <Reveal className="lp-survey-try"><span className="lp-survey-try-dot" />{t('surveyTry')}</Reveal>
            </div>
            <SurveyDemo />
          </div>
        </section>

        <div className="lp-paper is-flat">
          <section className="lp-section">
            <SectionHead
              index="04"
              eyebrow={t('setupEyebrow')}
              align="center"
              lines={t('setupLines')}
            />
            <PanelShowcase />
            <div className="lp-steps">
              {t('steps').map((s, i) => (
                <Reveal key={s.n} className="lp-step" delay={i * 0.12}>
                  <span className="lp-step-n">{s.n}</span>
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                </Reveal>
              ))}
            </div>
          </section>
        </div>

        <section className="lp-security" id="guvenlik">
          <div className="lp-security-inner">
            <SectionHead
              index="05"
              eyebrow={t('securityEyebrow')}
              dark
              lines={t('securityLines')}
              text={t('securityText')}
            />
            <Reveal className="lp-security-diagram">
              <ArchitectureDiagram />
            </Reveal>
            <div className="lp-security-grid">
              {t('security').map(([title, d], i) => (
                <Reveal key={title} className="lp-security-item" delay={(i % 3) * 0.08}>
                  <span className="lp-security-n">{String(i + 1).padStart(2, '0')}</span>
                  <h3>{title}</h3>
                  <p>{d}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <div className="lp-paper is-flat">
          <section className="lp-section" id="fiyatlar">
            <SectionHead index="06" eyebrow={t('pricingEyebrow')} align="center" lines={t('pricingLines')} />
            <PricingSection onDemo={openDemo} />
          </section>
          <section className="lp-section lp-faq-section" id="sss">
            <SectionHead index="07" eyebrow={t('faqEyebrow')} lines={t('faqLines')} />
            <div className="lp-faq">
              {t('faqs').map((f, i) => <FaqItem key={f.q} {...f} i={i} />)}
              <div className="lp-faq-contact">
                <span>{t('faqMissing')}</span>
                <a href="mailto:destek@martinci.com">destek@martinci.com →</a>
              </div>
            </div>
          </section>
        </div>

        <section className="lp-cta">
          <div className="lp-cta-glow" aria-hidden="true" />
          <Reveal className="lp-cta-kicker">{t('ctaKicker')}</Reveal>
          <MaskedHeading
            className="lp-cta-title"
            lines={t('ctaLines')}
          />
          <SignatureStroke className="lp-cta-stroke" delay={0.4} duration={2.6} />
          <Reveal className="lp-cta-actions" delay={0.2}>
            <Magnetic>
              <Link to="/basla" className="lp-btn lp-btn-lg lp-btn-light" data-cursor={t('ctaCursor')}>
                {t('startTrialLong')} <span className="lp-btn-arrow">→</span>
              </Link>
            </Magnetic>
            <button type="button" className="lp-btn-ghost" onClick={openDemo}>{t('bookDemo')}</button>
          </Reveal>
          <p className="lp-trial-note lp-trial-note-center">{t('trialNote')}</p>
        </section>

        <footer className="lp-footer">
          <div className="lp-footer-top">
            <img src={logo.src} alt={logo.alt} className="lp-footer-logo" />
            <nav className="lp-footer-links" aria-label={t('footerAria')}>
              {t('nav').map(([href, label]) => <a key={href} href={href}>{label}</a>)}
              <a href="mailto:destek@martinci.com">{t('contact')}</a>
            </nav>
            <nav className="lp-footer-links is-legal" aria-label={t('legalAria')}>
              {t('legalLinks').map(([to, label]) => <Link key={to} to={to}>{label}</Link>)}
            </nav>
          </div>
          <div className="lp-footer-bottom">
            <span>© {new Date().getFullYear()} Martinci Software &amp; Consulting</span>
            <span>{t('portalName')}</span>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default LandingPage;
